import { useEffect } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api } from '../lib/api.ts';
import { Cargando, EncabezadoPagina } from '../componentes/ui.tsx';
import { PasosPost } from '../componentes/PasosPost.tsx';

interface MarcaResumen {
  id: string;
  nombre: string;
  logoUrl: string | null;
  colorPrimario: string | null;
}

/** PASO 0 del flujo: el arte es de UNA marca. Con una sola marca comprada se salta solo. */
export function PaginaElegirMarca() {
  const { t } = useTranslation();
  const navegar = useNavigate();
  const busqueda = useSearch({ strict: false }) as { tipoContenido?: string };
  const tipoContenido = busqueda.tipoContenido ?? 'PROMOCION';

  const marcas = useQuery({
    queryKey: ['catalogo-marcas'],
    queryFn: () => api<{ items: MarcaResumen[] }>('/catalogo/marcas'),
    staleTime: 5 * 60 * 1000,
  });

  function elegir(id: string, reemplazar = false) {
    void navegar({
      to: '/productos',
      search: { tipoContenido, marca: id } as never,
      replace: reemplazar,
    });
  }

  // Una sola marca: nada que elegir, directo al diseño.
  const items = marcas.data?.items;
  useEffect(() => {
    if (items?.length === 1) elegir(items[0]!.id, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);

  return (
    <main className="mx-auto w-full max-w-[1600px] space-y-4">
      <EncabezadoPagina
        titulo={t('productos.elegirMarca')}
        subtitulo={t('productos.elegirMarcaSub')}
      />
      <div className="flex justify-center py-2">
        <PasosPost paso={1} />
      </div>
      {marcas.isPending ? <Cargando texto={t('comun.cargando')} /> : null}
      <ul className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
        {(items ?? []).map((m) => (
          <li key={m.id}>
            <button
              type="button"
              onClick={() => elegir(m.id)}
              className="tarjeta flex w-full flex-col items-center gap-3 p-6 text-center transition hover:-translate-y-0.5 hover:shadow-[var(--sombra-alta)]"
            >
              {m.logoUrl ? (
                <img src={m.logoUrl} alt={m.nombre} className="h-20 w-20 rounded-2xl bg-white object-contain p-2 shadow-[var(--sombra)]" />
              ) : (
                <span
                  className="flex h-20 w-20 items-center justify-center rounded-2xl text-3xl font-medium text-white"
                  style={{ backgroundColor: m.colorPrimario ?? 'var(--negro)' }}
                >
                  {m.nombre.charAt(0)}
                </span>
              )}
              <span className="text-[16px] font-medium">{m.nombre}</span>
            </button>
          </li>
        ))}
        {items?.length === 0 ? (
          <li className="tarjeta col-span-full p-6 text-sm text-suave">{t('productos.vacio')}</li>
        ) : null}
      </ul>
    </main>
  );
}
